// frontend/src/pages/purchase/EditPurchaseTransaction.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import {
  Package, Warehouse, Building, Calendar, Hash, DollarSign,
  ClipboardList, Percent, Trash2, User, Truck,
  CreditCard, FileText, Calculator, Save, X, Clock, AlertCircle,
  Loader2, RefreshCw, ArrowLeftRight, AlertTriangle,
  ArrowUp, ArrowDown, Download, Wallet,
  CreditCard as CreditCardIcon, Smartphone, Plus, Layers,
  Banknote, CircleDollarSign
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./PurchaseTransaction.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditPurchaseTransaction = () => {
  const { id } = useParams();
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();

  // State
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchaseTransaction, setPurchaseTransaction] = useState(null);

  // Dropdown data
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productStock, setProductStock] = useState({});
  const [paymentMethodsData, setPaymentMethodsData] = useState({
    methods: [],
  });
  const [discounts, setDiscounts] = useState([]);
  const [taxes, setTaxes] = useState([]);

  // Loading states
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  // Payment methods with account selection
  const [payments, setPayments] = useState([]);

  const getAccountsForMethod = (methodKey) =>
    paymentMethodsData.methods.find(
      (method) => method.key === methodKey,
    )?.accounts || [];

  // Form state
  const [formData, setFormData] = useState({
    warehouse_id: "",
    supplier_id: "",
    transaction_type_id: "",
    invoice_no: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0],
    notes: "",
    shipping_cost: "0",
    other_charges: "0",
    return_reason: "",
  });

  // Hidden status fields
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [transactionStatus, setTransactionStatus] = useState("pending");
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [originalFinancialSnapshot, setOriginalFinancialSnapshot] = useState(null);

  // Complete invoice product lines
  const [productsData, setProductsData] = useState([]);

  // Purchase-level discount and tax controls – manual_tax removed
  const [purchaseAdjustments, setPurchaseAdjustments] = useState({
    discount_id: "",
    tax_id: "",
    manual_discount: "",
    // manual_tax removed
  });

  // Errors & focus
  const [focusedFields, setFocusedFields] = useState({});
  const [errors, setErrors] = useState({});

  // Totals
  const [totals, setTotals] = useState({
    subTotal: 0,
    totalDiscount: 0,
    totalTax: 0,
    shippingCost: 0,
    otherCharges: 0,
    grandTotal: 0,
    totalAmountPaid: 0,
    netAmount: 0,
  });

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isReturn = purchaseTransaction?.is_return || false;
  const isReturnModeRef = useRef(isReturn);

  const isFinanciallyPosted = Boolean(
    purchaseTransaction?.accounting_status === "posted" ||
    purchaseTransaction?.journal_transaction_id ||
    purchaseTransaction?.accounting_transaction_id ||
    purchaseTransaction?.inventory_posted ||
    purchaseTransaction?.stock_posted ||
    purchaseTransaction?.invoice?.accounting_status === "posted" ||
    purchaseTransaction?.invoice?.journal_transaction_id
  );

  const canUnlockFinancialEditing =
    isSystemAdmin ||
    currentUser?.role === "finance_manager" ||
    currentUser?.role === "accountant" ||
    currentUser?.permissions?.includes?.("purchase.financial_edit");

  // Helper: get company ID
  const getCompanyId = () => {
    if (purchaseTransaction) {
      return purchaseTransaction.company_id?._id || purchaseTransaction.company_id;
    }
    if (userType === 'company' && company) {
      return company._id || company;
    }
    return null;
  };

  // Load grouped payment methods, accounts, discounts and taxes.
  const fetchPurchaseOptions = async (companyId) => {
    if (!companyId) return;

    try {
      setLoadingPaymentMethods(true);

      const response = await axios.get(
        `${url}/api/purchaseorders/payment-methods`,
        {
          params: { company_id: companyId },
        },
      );

      const options = response.data?.data || {};

      setPaymentMethodsData({
        methods: Array.isArray(options.methods)
          ? options.methods
          : [],
      });
      setDiscounts(
        Array.isArray(options.discounts)
          ? options.discounts
          : [],
      );
      setTaxes(
        Array.isArray(options.taxes)
          ? options.taxes
          : [],
      );

      return {
        methods: Array.isArray(options.methods)
          ? options.methods
          : [],
        discounts: Array.isArray(options.discounts)
          ? options.discounts
          : [],
        taxes: Array.isArray(options.taxes)
          ? options.taxes
          : [],
      };
    } catch (error) {
      setPaymentMethodsData({ methods: [] });
      setDiscounts([]);
      setTaxes([]);
      toast.error(
        error.response?.data?.message ||
        "Failed to load payment methods, discounts, and taxes",
      );

      return {
        methods: [],
        discounts: [],
        taxes: [],
      };
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // ========== FIX: Fetch products for warehouse and filter by available stock ==========
  const fetchProductsForWarehouse = async (warehouseId, sourceProducts = products) => {
    if (!warehouseId) {
      setFilteredProducts(sourceProducts);
      setProductStock({});
      return;
    }
    try {
      setLoadingProducts(true);
      const companyId = getCompanyId();
      const response = await axios.get(`${url}/api/inventorystock/warehouse/${warehouseId}`, {
        params: { company_id: companyId, status: 'active' }
      });
      if (response.data.success) {
        const stockMap = {};

        (response.data.data || []).forEach((stock) => {
          const product = stock.product_id || {};
          const productId = product._id || stock.product_id;

          if (!productId) return;

          stockMap[String(productId)] = {
            current_stock: Number(stock.quantity || 0),
            available_stock: Number(
              stock.available_qty ??
              stock.quantity ??
              0
            ),
            avg_cost: Number(stock.avg_cost || 0),
          };
        });

        const mergedProducts = sourceProducts.map((product) => {
          const productId =
            product._id || product.product_id;
          const stock =
            stockMap[String(productId)] || {};

          return {
            ...product,
            _id: productId,
            product_id: productId,
            product_code:
              product.product_code ||
              product.code ||
              "N/A",
            product_name:
              product.product_name ||
              product.name ||
              "Unknown",
            current_stock: Number(
              stock.current_stock || 0
            ),
            available_stock: Number(
              stock.available_stock || 0
            ),
            avg_cost: Number(
              stock.avg_cost ||
              product.purchase_cost ||
              product.cost_price ||
              0
            ),
          };
        });

        setProductStock(stockMap);

        // ✅ NEW: Only show products with available stock > 0
        const filteredMerged = mergedProducts.filter(
          (product) => product.available_stock > 0
        );
        setFilteredProducts(filteredMerged);
      } else {
        setFilteredProducts(sourceProducts);
        setProductStock({});
      }
    } catch (error) {
      setFilteredProducts(sourceProducts);
      setProductStock({});
    } finally {
      setLoadingProducts(false);
    }
  };

  // ========== Load stock when warehouse is preselected (edit mode) ==========
  useEffect(() => {
    if (warehouses.length > 0 && formData.warehouse_id) {
      fetchProductsForWarehouse(formData.warehouse_id, products);
    }
  }, [warehouses, formData.warehouse_id]);

  // Main data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const transactionRes = await axios.get(`${url}/api/purchase/${id}`, {
          timeout: 10000
        });
        const transaction = transactionRes.data.data;
        const invoiceData = transaction.invoice || {};
        setPurchaseTransaction(transaction);
        isReturnModeRef.current = transaction.is_return || false;
        setOriginalFinancialSnapshot({
          warehouse_id: transaction.warehouse_id?._id || transaction.warehouse_id || "",
          supplier_id: transaction.supplier_id?._id || transaction.supplier_id || "",
          transaction_type_id: transaction.transaction_type_id?._id || transaction.transaction_type_id || "",
          invoice_no: invoiceData.invoice_no || transaction.invoice_no || "",
          transaction_date: invoiceData.invoice_date || transaction.transaction_date || transaction.invoice_date || null,
          product_id: transaction.product_id?._id || transaction.product_id || "",
          quantity: Math.abs(Number(transaction.quantity) || 0),
          unit_cost: Number(transaction.unit_cost) || 0,
          discount_id: transaction.discount_id?._id || transaction.discount_id || null,
          tax_id: transaction.tax_id?._id || transaction.tax_id || null,
          discount_amount: Number(transaction.discount_amount) || 0,
          tax_amount: Number(transaction.tax_amount) || 0,
          shipping_cost: Number(invoiceData.shipping_cost ?? transaction.shipping_cost) || 0,
          other_charges: Number(invoiceData.other_charges ?? transaction.other_charges) || 0,
          total_amount: Number(invoiceData.grand_total ?? transaction.grand_total ?? transaction.total_amount ?? transaction.net_amount) || 0,
          total_amount_paid: Number(invoiceData.total_amount_paid ?? transaction.total_amount_paid) || 0,
          payment_details: invoiceData.payment_details || transaction.payment_details || transaction.payments || [],
          transactions: (transaction.invoice_transactions || []).map((line) => ({
            _id: line._id,
            product_id: line.product_id?._id || line.product_id,
            warehouse_id: line.warehouse_id?._id || line.warehouse_id,
            supplier_id: line.supplier_id?._id || line.supplier_id,
            quantity: Math.abs(Number(line.quantity) || 0),
            unit_cost: Number(line.unit_cost) || 0,
            discount_id: line.discount_id?._id || line.discount_id || null,
            tax_id: line.tax_id?._id || line.tax_id || null,
            discount_amount: Number(line.discount_amount) || 0,
            tax_amount: Number(line.tax_amount) || 0,
          })),
        });

        const companyId =
          transaction.company_id?._id ||
          transaction.company_id;

        const [loadedProducts, loadedPurchaseOptions] = await Promise.all([
          fetchAllDropdownData(companyId),
          fetchPurchaseOptions(companyId),
        ]);

        const paymentDetails =
          invoiceData.payment_details ||
          transaction.payment_details ||
          transaction.payments ||
          [];

        if (paymentDetails.length > 0) {
          setPayments(
            paymentDetails.map((payment) => ({
              method: payment.method || "credit",
              account_id:
                payment.account_id?._id ||
                payment.account_id ||
                "",
              account_name:
                payment.account_name ||
                payment.account_id?.account_name ||
                "",
              amount:
                payment.method === "credit"
                  ? ""
                  : String(payment.amount || ""),
              reference:
                payment.reference ||
                payment.reference_no ||
                "",
            })),
          );
        } else {
          const invoicePaymentMethod =
            invoiceData.payment_method ?? transaction.payment_method;
          const methods = Array.isArray(invoicePaymentMethod)
            ? invoicePaymentMethod
            : invoicePaymentMethod
              ? [invoicePaymentMethod]
              : ["credit"];

          setPayments(
            methods.map((method) => ({
              method,
              account_id: "",
              account_name: "",
              amount:
                method === "credit"
                  ? ""
                  : String(invoiceData.total_amount_paid ?? transaction.total_amount_paid ?? ""),
              reference: "",
            })),
          );
        }

        setFormData({
          warehouse_id: transaction.warehouse_id?._id || transaction.warehouse_id || "",
          supplier_id: transaction.supplier_id?._id || transaction.supplier_id || "",
          transaction_type_id: transaction.transaction_type_id?._id || transaction.transaction_type_id || "",
          invoice_no: invoiceData.invoice_no || transaction.invoice_no || "",
          invoice_date: (invoiceData.invoice_date || transaction.transaction_date)
            ? new Date(invoiceData.invoice_date || transaction.transaction_date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          due_date: (transaction.invoice?.due_date || transaction.due_date)
            ? new Date(transaction.invoice?.due_date || transaction.due_date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          notes: transaction.notes || "",
          shipping_cost: (transaction.invoice?.shipping_cost ?? transaction.shipping_cost)?.toString() || "0",
          other_charges: (transaction.invoice?.other_charges ?? transaction.other_charges)?.toString() || "0",
          return_reason: transaction.return_reason || "",
        });

        setPaymentStatus(invoiceData.payment_status || transaction.payment_status || "pending");
        setTransactionStatus(transaction.status || "pending");

        const invoiceLines = Array.isArray(transaction.invoice_transactions) && transaction.invoice_transactions.length
          ? transaction.invoice_transactions
          : [transaction];

        setProductsData(invoiceLines.map((line) => ({
          _id: line._id,
          product_id: line.product_id?._id || line.product_id || "",
          quantity: Math.abs(Number(line.quantity) || 0).toString(),
          unit_cost: Number(line.unit_cost || 0).toFixed(2),
          discount_id:
            line.discount_id?._id || line.discount_id ||
            (Number(line.discount_amount) > 0 ? "__legacy_discount__" : ""),
          tax_id:
            line.tax_id?._id || line.tax_id ||
            (Number(line.tax_amount) > 0 ? "__legacy_tax__" : ""),
          discount_percent: Number(line.discount_percent || 0).toString(),
          discount_flat: Number(line.discount_flat || 0).toString(),
          discount_amount: Number(line.discount_amount || 0).toString(),
          tax_amount: Number(line.tax_amount || 0).toString(),
          total_cost: Math.abs(Number(line.grand_total ?? line.total_cost ?? 0)).toString(),
          batch_number: line.batch_number || "",
          expiry_date: line.expiry_date
            ? new Date(line.expiry_date).toISOString().split('T')[0]
            : "",
          notes: line.notes || "",
        })));

        const firstInvoiceLine = invoiceLines[0] || {};
        const savedDiscountId =
          invoiceData.discount_id?._id ||
          invoiceData.discount_id ||
          firstInvoiceLine.discount_id?._id ||
          firstInvoiceLine.discount_id ||
          "";

        let savedTaxId =
          invoiceData.tax_id?._id ||
          invoiceData.tax_id ||
          firstInvoiceLine.tax_id?._id ||
          firstInvoiceLine.tax_id ||
          "";

        if (!savedTaxId) {
          const availableTaxes = Array.isArray(loadedPurchaseOptions?.taxes)
            ? loadedPurchaseOptions.taxes
            : [];

          const savedSubtotal = Number(
            invoiceData.subtotal ??
            invoiceData.sub_total ??
            invoiceLines.reduce(
              (sum, line) =>
                sum +
                Math.abs(Number(line.quantity) || 0) *
                (Number(line.unit_cost) || 0),
              0,
            ),
          ) || 0;

          const savedDiscount = Number(
            invoiceData.total_discount || 0,
          ) || 0;

          const savedTaxAmount = Number(
            invoiceData.total_tax ??
            invoiceData.tax_amount ??
            invoiceLines.reduce(
              (sum, line) =>
                sum + (Number(line.tax_amount) || 0),
              0,
            ),
          ) || 0;

          const taxableBase = Math.max(
            0,
            savedSubtotal - savedDiscount,
          );

          const matchingTax = availableTaxes.find((tax) => {
            if (
              tax?.tax_type !== "percentage" ||
              taxableBase <= 0 ||
              savedTaxAmount <= 0
            ) {
              return false;
            }

            const rate = Number(tax.value || 0);
            const expectedTax = tax.price_inclusive
              ? taxableBase -
              taxableBase / (1 + rate / 100)
              : (taxableBase * rate) / 100;

            return (
              Math.abs(
                Math.round(expectedTax * 100) / 100 -
                Math.round(savedTaxAmount * 100) / 100,
              ) <= 0.005
            );
          });

          savedTaxId = matchingTax?._id || "";
        }

        // ⭐ CHANGED: prefer the explicitly stored manual_discount
        const savedManualDiscount = Number(invoiceData.manual_discount ?? 0);

        setPurchaseAdjustments({
          discount_id: savedDiscountId ? String(savedDiscountId) : "",
          tax_id: savedTaxId ? String(savedTaxId) : "",
          manual_discount:
            savedManualDiscount > 0
              ? String(savedManualDiscount)
              : (!savedDiscountId && Number(invoiceData.total_discount || 0) > 0
                ? String(Number(invoiceData.total_discount || 0))
                : ""),
        });

        if (transaction.warehouse_id?._id || transaction.warehouse_id) {
          const warehouseId = transaction.warehouse_id?._id || transaction.warehouse_id;
          await fetchProductsForWarehouse(warehouseId, loadedProducts);
        }
      } catch (error) {
        setError(error.message);
        let errorMsg = "Failed to load purchase transaction";
        if (error.response) {
          errorMsg = error.response.data?.message || errorMsg;
          if (error.response.status === 404) errorMsg = "Purchase transaction not found";
        }
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && id) fetchData();
  }, [currentUser, id, navigate]);

  // Fetch all dropdown data (warehouses, suppliers, transaction types, products)
  const fetchAllDropdownData = async (companyId) => {
    if (!companyId) return;
    try {
      const params = { company_id: companyId, status: 'active' };

      const whRes = await axios.get(`${url}/api/warehouse/dropdown`, { params });
      if (whRes.data?.success) setWarehouses(whRes.data.data || []);

      const supRes = await axios.get(`${url}/api/supplier/dropdown`, { params });
      if (supRes.data?.success) setSuppliers(supRes.data.data || []);

      const ttRes = await axios.get(`${url}/api/transactiontype/dropdown`, { params });
      if (ttRes.data?.success) {
        const allTypes = ttRes.data.data || [];
        const purchaseTypes = allTypes.filter(type => {
          const typeName = (type.transaction_type || '').toLowerCase();
          return typeName === 'purchase' || typeName === 'purchase_return' ||
            (typeName.includes('purchase') && !typeName.includes('opening')) ||
            (typeName.includes('return') && type.sign === '-');
        });
        setTransactionTypes(purchaseTypes);
      }

      const prodRes = await axios.get(`${url}/api/products/dropdown`, {
        params,
      });

      const loadedProducts = prodRes.data?.success
        ? prodRes.data.data || []
        : [];

      setProducts(loadedProducts);
      setFilteredProducts(loadedProducts);

      return loadedProducts;
    } catch (error) {
      setProducts([]);
      setFilteredProducts([]);
      return [];
    }
  };

  // Handle warehouse change
  const handleWarehouseChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (value) {
      fetchProductsForWarehouse(value, products);
    } else {
      setFilteredProducts(products);
      setProductStock({});
    }
  };

  const roundMoney = (value) =>
    Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

  const allocateDocumentAmount = (
    amount,
    lineIndex,
    sourceProducts = productsData,
  ) => {
    const grossValues = sourceProducts.map(
      (row) =>
        (Number(row.quantity) || 0) *
        (Number(row.unit_cost) || 0),
    );
    const totalGross = grossValues.reduce(
      (sum, value) => sum + value,
      0,
    );
    const safeAmount = Math.max(0, Number(amount) || 0);

    if (
      safeAmount <= 0 ||
      totalGross <= 0 ||
      lineIndex < 0
    ) {
      return 0;
    }

    if (lineIndex === grossValues.length - 1) {
      const allocatedBefore = grossValues
        .slice(0, lineIndex)
        .reduce(
          (sum, gross) =>
            sum +
            roundMoney(
              (safeAmount * gross) / totalGross,
            ),
          0,
        );

      return Math.max(
        0,
        roundMoney(safeAmount - allocatedBefore),
      );
    }

    return roundMoney(
      (safeAmount * grossValues[lineIndex]) /
      totalGross,
    );
  };

  const calculateProductLine = (
    product,
    suppliedIndex = -1,
    sourceProducts = productsData,
  ) => {
    const quantity = Number(product.quantity) || 0;
    const unitCost = Number(product.unit_cost) || 0;
    const gross = roundMoney(quantity * unitCost);
    const lineIndex =
      suppliedIndex >= 0
        ? suppliedIndex
        : sourceProducts.indexOf(product);

    const discount = discounts.find(
      (row) =>
        String(row._id) ===
        String(purchaseAdjustments.discount_id),
    );
    const tax = taxes.find(
      (row) =>
        String(row._id) ===
        String(purchaseAdjustments.tax_id),
    );

    let selectedDiscountAmount = 0;

    if (discount?.discount_type === "percentage") {
      selectedDiscountAmount = roundMoney(
        (gross * Number(discount.value || 0)) / 100,
      );
    } else if (discount?.discount_type === "fixed") {
      selectedDiscountAmount = allocateDocumentAmount(
        discount.value,
        lineIndex,
        sourceProducts,
      );
    }

    const manualDiscount = allocateDocumentAmount(
      purchaseAdjustments.manual_discount,
      lineIndex,
      sourceProducts,
    );

    const discountAmount = roundMoney(
      Math.min(
        gross,
        selectedDiscountAmount + manualDiscount,
      ),
    );
    const afterDiscount = roundMoney(
      Math.max(0, gross - discountAmount),
    );

    let selectedTaxAmount = 0;
    let exclusiveTax = 0;

    if (tax?.tax_type === "percentage") {
      const rate = Number(tax.value || 0);

      selectedTaxAmount = tax.price_inclusive
        ? roundMoney(
          afterDiscount -
          afterDiscount / (1 + rate / 100),
        )
        : roundMoney(
          (afterDiscount * rate) / 100,
        );

      exclusiveTax = tax.price_inclusive
        ? 0
        : selectedTaxAmount;
    } else if (tax?.tax_type === "fixed") {
      selectedTaxAmount = allocateDocumentAmount(
        tax.value,
        lineIndex,
        sourceProducts,
      );
      exclusiveTax = selectedTaxAmount;
    }

    // manual_tax removed – only selected tax is used
    const taxAmount = roundMoney(selectedTaxAmount);
    exclusiveTax = roundMoney(exclusiveTax);

    return {
      gross,
      discountAmount,
      taxAmount,
      exclusiveTax,
      total: roundMoney(
        afterDiscount + exclusiveTax,
      ),
      discountPercent:
        discount?.discount_type === "percentage"
          ? Number(discount.value || 0)
          : 0,
      discountFlat: 0,
    };
  };

  // Apply purchase-level selections to every item.
  useEffect(() => {
    const selectedDiscount = discounts.find(
      (row) =>
        String(row._id) ===
        String(purchaseAdjustments.discount_id),
    );
    const selectedTax = taxes.find(
      (row) =>
        String(row._id) ===
        String(purchaseAdjustments.tax_id),
    );

    setProductsData((current) => {
      let changed = false;

      const next = current.map((row, index) => {
        const calculated = calculateProductLine(
          row,
          index,
          current,
        );

        const updated = {
          ...row,
          discount_id:
            purchaseAdjustments.discount_id || "",
          tax_id: purchaseAdjustments.tax_id || "",
          discount_percent: String(
            calculated.discountPercent,
          ),
          discount_flat: String(
            calculated.discountFlat,
          ),
          discount_amount:
            calculated.discountAmount.toFixed(2),
          tax_amount: calculated.taxAmount.toFixed(2),
          total_cost: calculated.total.toFixed(2),
        };

        if (
          updated.discount_id !== row.discount_id ||
          updated.tax_id !== row.tax_id ||
          updated.discount_percent !==
          row.discount_percent ||
          updated.discount_flat !== row.discount_flat ||
          updated.discount_amount !==
          row.discount_amount ||
          updated.tax_amount !== row.tax_amount ||
          updated.total_cost !== row.total_cost
        ) {
          changed = true;
        }

        return updated;
      });

      return changed ? next : current;
    });
  }, [
    purchaseAdjustments,
    discounts,
    taxes,
    productsData
      .map(
        (row) =>
          `${row.quantity}:${row.unit_cost}`,
      )
      .join("|"),
  ]);

  // Calculate totals
  useEffect(() => {
    let subTotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    let exclusiveTax = 0;

    productsData.forEach((product, index) => {
      const calculated = calculateProductLine(
        product,
        index,
        productsData,
      );
      subTotal += calculated.gross;
      totalDiscount += calculated.discountAmount;
      totalTax += calculated.taxAmount;
      exclusiveTax += calculated.exclusiveTax;
    });

    const shippingCost =
      Number(formData.shipping_cost) || 0;
    const otherCharges =
      Number(formData.other_charges) || 0;

    const grandTotal =
      subTotal -
      totalDiscount +
      exclusiveTax +
      shippingCost +
      otherCharges;

    const totalAmountPaid = payments.reduce(
      (sum, payment) =>
        sum +
        (payment.method === "credit"
          ? 0
          : Number(payment.amount) || 0),
      0,
    );

    const netAmount = Math.max(
      grandTotal - totalAmountPaid,
      0,
    );

    let nextPaymentStatus = "notpaid";

    if (totalAmountPaid > 0) {
      nextPaymentStatus =
        totalAmountPaid >= grandTotal - 0.005
          ? "paid"
          : "partial";
    }

    setPaymentStatus(nextPaymentStatus);

    setTotals({
      subTotal,
      totalDiscount,
      totalTax,
      shippingCost,
      otherCharges,
      grandTotal: isReturnModeRef.current
        ? -Math.abs(grandTotal)
        : grandTotal,
      totalAmountPaid,
      netAmount: isReturnModeRef.current
        ? -Math.abs(netAmount)
        : netAmount,
    });
  }, [
    productsData,
    formData.shipping_cost,
    formData.other_charges,
    payments,
    discounts,
    taxes,
  ]);

  // Payment handlers
  const addPaymentRow = () => {
    setPayments((previous) => [
      ...(Array.isArray(previous) ? previous : []),
      {
        method: "cash",
        account_id: "",
        account_name: "",
        amount: "",
        reference: "",
      },
    ]);

    setErrors((previous) => ({
      ...previous,
      payments: "",
    }));
  };

  const removePaymentRow = (index) => {
    if (payments.length > 1) {
      setPayments((previous) =>
        previous.filter((_, rowIndex) => rowIndex !== index),
      );
    } else {
      toast.warning("At least one payment method is required");
    }
  };

  const handlePaymentChange = (index, field, value) => {
    const updated = payments.map((payment) => ({
      ...payment,
    }));

    if (field === "amount") {
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        updated[index].amount = value;
      }
    } else if (field === "method") {
      updated[index].method = value;
      updated[index].account_id = "";
      updated[index].account_name = "";

      if (value === "credit") {
        updated[index].amount = "";
      }
    } else if (field === "account_id") {
      updated[index].account_id = value;

      const selectedAccount = getAccountsForMethod(
        updated[index].method,
      ).find(
        (account) => String(account._id) === String(value),
      );

      updated[index].account_name =
        selectedAccount?.account_name || "";
    } else {
      updated[index][field] = value;
    }

    setPayments(updated);

    if (errors.payments) {
      setErrors((previous) => ({
        ...previous,
        payments: "",
      }));
    }
  };

  // Product handlers
  const addProductRow = () => {
    setProductsData(prev => [...prev, {
      _id: null,
      product_id: "",
      quantity: "",
      unit_cost: "",
      discount_id: "",
      tax_id: "",
      discount_percent: "0",
      discount_flat: "0",
      discount_amount: "0",
      tax_amount: "0",
      total_cost: "0",
      batch_number: "", expiry_date: "", notes: ""
    }]);
  };

  const removeProductRow = (index) => {
    if (productsData.length > 1) setProductsData(prev => prev.filter((_, i) => i !== index));
    else toast.warning("At least one product is required");
  };

  const handleFocus = (field) => setFocusedFields(prev => ({ ...prev, [field]: true }));
  const handleBlur = (field) => {
    const value = formData[field];
    setFocusedFields(prev => ({ ...prev, [field]: (typeof value === 'string' ? value.trim() : value) ? true : false }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'warehouse_id') handleWarehouseChange(e);
    else if (['shipping_cost', 'other_charges'].includes(name)) {
      if (value === '' || /^\d*\.?\d*$/.test(value)) setFormData(prev => ({ ...prev, [name]: value }));
    } else setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleProductSelect = async (index, productId) => {
    try {
      // Prevent same product + same warehouse duplicate
      const duplicate = productsData.some(
        (row, rowIndex) =>
          rowIndex !== index &&
          String(row.product_id) === String(productId) &&
          String(productId) !== ""
      );

      if (duplicate) {
        toast.error(
          "This product is already added. Same product cannot appear twice."
        );

        setProductsData((previous) => {
          const updated = [...previous];
          updated[index] = {
            ...updated[index],
            product_id: "",
            quantity: "",
            unit_cost: "",
            total_cost: "0",
          };
          return updated;
        });

        return;
      }

      setProductsData((previous) => {
        const updated = [...previous];
        updated[index].product_id = productId;
        return updated;
      });

      if (productId) {
        const stockInfo = productStock[productId];
        let avgCost = 0;

        if (stockInfo && stockInfo.avg_cost > 0) {
          avgCost = stockInfo.avg_cost;
        } else {
          const companyId = getCompanyId();
          const response = await axios.get(
            `${url}/api/products/${productId}`,
            {
              params: { company_id: companyId },
            },
          );
          if (response.data.success) {
            const product = response.data.data;
            avgCost =
              product.purchase_cost ||
              product.cost_price ||
              0;
          }
        }

        setProductsData((previous) => {
          const updated = previous.map((product) => ({
            ...product,
          }));

          updated[index].unit_cost =
            Number(avgCost || 0).toFixed(2);

          const calculated = calculateProductLine(
            updated[index],
            index,
            updated,
          );

          updated[index].discount_percent =
            String(calculated.discountPercent);
          updated[index].discount_flat =
            String(calculated.discountFlat);
          updated[index].discount_amount =
            calculated.discountAmount.toFixed(2);
          updated[index].tax_amount =
            calculated.taxAmount.toFixed(2);
          updated[index].total_cost =
            calculated.total.toFixed(2);

          return updated;
        });
      }
    } catch (error) {
      toast.error("Failed to load product information");
    }
  };

  const handleProductChange = (index, field, value) => {
    setProductsData((previous) => {
      const updated = previous.map((product) => ({
        ...product,
      }));

      if (
        ["quantity", "unit_cost"].includes(field) &&
        value !== "" &&
        !/^\d*\.?\d*$/.test(value)
      ) {
        return previous;
      }

      updated[index][field] = value;

      const calculated = calculateProductLine(updated[index], index, updated);

      updated[index].discount_percent =
        String(calculated.discountPercent);
      updated[index].discount_flat =
        String(calculated.discountFlat);
      updated[index].discount_amount =
        calculated.discountAmount.toFixed(2);
      updated[index].tax_amount =
        calculated.taxAmount.toFixed(2);
      updated[index].total_cost =
        calculated.total.toFixed(2);

      return updated;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.warehouse_id) newErrors.warehouse_id = "Warehouse is required";
    if (!formData.supplier_id) newErrors.supplier_id = "Supplier is required";
    if (!formData.transaction_type_id) newErrors.transaction_type_id = "Transaction type is required";
    if (isReturnModeRef.current && !formData.return_reason) newErrors.return_reason = "Return reason is required";
    if (
      isFinanciallyPosted &&
      adjustmentReason.trim().length < 10
    ) {
      newErrors.adjustment_reason =
        "Enter a clear adjustment reason of at least 10 characters";
    }

    const validProducts = productsData.filter(p => p.product_id && p.quantity && p.unit_cost && parseFloat(p.quantity) > 0 && parseFloat(p.unit_cost) >= 0);
    if (validProducts.length === 0) newErrors.products = "At least one valid product is required";
    else {
      productsData.forEach((product, index) => {
        if (product.product_id && (!product.quantity || parseFloat(product.quantity) <= 0)) newErrors[`quantity_${index}`] = "Quantity must be greater than 0";
        if (product.product_id && (!product.unit_cost || parseFloat(product.unit_cost) < 0)) newErrors[`unit_cost_${index}`] = "Unit cost cannot be negative";
      });
    }

    const paidPayments = payments.filter(
      (payment) =>
        payment.method !== "credit" &&
        payment.amount &&
        Number(payment.amount) > 0,
    );

    const paymentWithoutAccount = paidPayments.find(
      (payment) => !payment.account_id,
    );
    if (paymentWithoutAccount) {
      const methodName = String(paymentWithoutAccount.method || "payment")
        .replaceAll("_", " ");
      newErrors.payments = `Please select an account for ${methodName}`;
    }

    const paidTotal = paidPayments.reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0,
    );

    // ✅ NEW: overpayment / over-refund validation with amounts in the message
    if (paidTotal > Math.abs(totals.grandTotal) + 0.005) {
      const excess = paidTotal - Math.abs(totals.grandTotal);
      const label = isReturnModeRef.current ? "Refund" : "Payment";
      newErrors.payments =
        `${label} exceeds the total by ${formatCurrency(excess)}. ` +
        `Total: ${formatCurrency(Math.abs(totals.grandTotal))} • ` +
        `Entered: ${formatCurrency(paidTotal)}. ` +
        `Reduce the ${label.toLowerCase()} by ${formatCurrency(excess)} to continue.`;
    }

    setErrors(newErrors);

    const errorKeys = Object.keys(newErrors);

    if (errorKeys.length > 0) {
      const firstMessage = newErrors[errorKeys[0]];

      toast.error(
        firstMessage || "Please complete the required fields"
      );

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return false;
    }

    return true;
  };

  const handleCancel = () => navigate("/ViewPurchaseTransaction");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    setSubmitting(true);
    try {
      const validPayments = payments.filter(
        (payment) =>
          payment.method !== "credit" &&
          Number(payment.amount || 0) > 0 &&
          payment.account_id,
      );
      const validProducts = productsData.filter(p => p.product_id && p.quantity && p.unit_cost && parseFloat(p.quantity) > 0 && parseFloat(p.unit_cost) >= 0);

      // Build transactions array with _id for existing lines, null for new
      const transactions = validProducts.map((product, index) => {
        const calculated = calculateProductLine(
          product,
          index,
          validProducts,
        );

        return {
          _id: product._id || null,
          product_id: product.product_id,
          warehouse_id: formData.warehouse_id,
          supplier_id: formData.supplier_id,
          quantity: Number(product.quantity),
          unit_cost: Number(product.unit_cost),
          discount_id: purchaseAdjustments.discount_id || null,
          tax_id: purchaseAdjustments.tax_id || null,
          discount_percent: calculated.discountPercent,
          discount_flat: calculated.discountFlat,
          discount_amount: calculated.discountAmount,
          tax_amount: calculated.taxAmount,
          batch_number: product.batch_number || "",
          expiry_date: product.expiry_date || null,
          notes: product.notes || "",
        };
      });

      const payload = {
        warehouse_id: formData.warehouse_id,
        supplier_id: formData.supplier_id,
        invoice_no: formData.invoice_no,
        invoice_date: formData.invoice_date,
        due_date: formData.due_date,
        notes: formData.notes,
        discount_id: purchaseAdjustments.discount_id || null,
        tax_id: purchaseAdjustments.tax_id || null,
        manual_discount: Number(purchaseAdjustments.manual_discount || 0),
        shipping_cost: parseFloat(formData.shipping_cost) || 0,
        other_charges: parseFloat(formData.other_charges) || 0,
        payment_status: paymentStatus,
        total_amount_paid: totals.totalAmountPaid,
        net_amount: Math.abs(totals.netAmount),
        payment_details: validPayments.map((payment) => ({
          method: payment.method,
          account_id: payment.account_id,
          amount: Number(payment.amount),
          reference: payment.reference || "",
        })),
        transactions: transactions,
        return_reason: formData.return_reason,
        financial_edit_mode: Boolean(isFinanciallyPosted),
        adjustment_reason: isFinanciallyPosted ? adjustmentReason.trim() : "",
        original_financial_snapshot: isFinanciallyPosted ? originalFinancialSnapshot : undefined,
        update_inventory: true,
        inventory_update_strategy: isFinanciallyPosted ? "delta" : "replace",
        payments: validPayments.map((payment) => ({
          method: payment.method,
          account_id: payment.account_id,
          account_name: payment.account_name || "",
          amount: Number(payment.amount),
          reference: payment.reference || "",
        })),
      };

      const res = await axios.put(`${url}/api/purchase/${id}`, payload, {
        headers: { "Content-Type": "application/json" }
      });

      if (res.data.success) {
        toast.success(
          res.data?.message ||
          "Purchase, stock and accounting adjustment saved successfully"
        );
        setTimeout(() => navigate("/ViewPurchaseTransaction"), 1500);
      }
    } catch (error) {
      let errorMsg = "Failed to update purchase transaction";
      if (error.response) {
        errorMsg = error.response.data?.message || errorMsg;
        if (error.response.data?.errors) {
          const serverErrors = {};
          if (Array.isArray(error.response.data.errors)) {
            error.response.data.errors.forEach(err => { if (err.field) serverErrors[err.field] = err.message; });
          } else if (typeof error.response.data.errors === 'object') {
            Object.keys(error.response.data.errors).forEach(key => { serverErrors[key] = error.response.data.errors[key]; });
          }
          setErrors(serverErrors);
        }
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return "Invalid date"; }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = { cash: 'Cash', bank: 'Bank', mobile_money: 'Mobile Money', credit: 'Credit' };
    return labels[method] || method;
  };

  const getPaymentStatusDisplayInfo = () => {
    if (paymentStatus === 'paid') return { text: 'Paid', color: '#52c41a', bg: '#f6ffed' };
    if (paymentStatus === 'partial') return { text: 'Partial', color: '#fa8c16', bg: '#fff7e6' };
    if (paymentStatus === 'notpaid') return { text: 'Not Paid', color: '#ff4d4f', bg: '#fff2f0' };
    return { text: paymentStatus || 'Pending', color: '#856404', bg: '#fff3cd' };
  };

  // ✅ NEW: overpayment / over-refund detection — works in BOTH Purchase and Return mode
  const overpaidAmount = Math.max(
    0,
    totals.totalAmountPaid - Math.abs(totals.grandTotal)
  );
  const hasOverpayment = overpaidAmount > 0.005;

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="purchase-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading purchase transaction data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !purchaseTransaction) {
    return (
      <div className="form-wrapper">
        <div className="purchase-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h2>Transaction Not Found</h2>
            <p>{error || "Purchase transaction not found"}</p>
            <button className="cancel-btn" onClick={handleCancel} style={{ marginTop: '20px' }}>Back to List</button>
          </div>
        </div>
      </div>
    );
  }

  const paymentStatusDisplay = getPaymentStatusDisplayInfo();

  return (
    <div className="form-wrapper">
      <div className="purchase-form-container">
        <button className="close-btn" onClick={handleCancel} title="Go Back" type="button"><X size={20} /></button>
        <div className="form-header">
          <ClipboardList size={24} />
          <h2 className="form-title">Edit {isReturn ? 'Purchase Return' : 'Purchase Transaction'}</h2>
          <p className="form-subtitle">Update transaction details for {purchaseTransaction.reference_id}</p>
        </div>

        {isReturn && (
          <div style={{ padding: '12px 20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', background: '#fff2f0', border: '1px solid #ffccc7', color: '#ff4d4f' }}>
            <AlertTriangle size={20} />
            <span style={{ fontWeight: 600 }}>RETURN TRANSACTION - Editing a purchase return</span>
            <ArrowDown size={16} />
          </div>
        )}

        <div style={{ background: '#f0f9ff', border: '1px solid #bae7ff', borderRadius: '8px', padding: '16px 20px', marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <div><Hash size={14} color="#1890ff" /> <strong>Reference ID:</strong> {purchaseTransaction.reference_id || "N/A"}</div>
          <div><FileText size={14} color="#1890ff" /> <strong>Invoice No:</strong> {purchaseTransaction.invoice_no || "N/A"}</div>
          <div><Building size={14} color="#1890ff" /> <strong>Company:</strong> {purchaseTransaction.company_id?.company_name || purchaseTransaction.company_id}</div>
          <div><User size={14} color="#1890ff" /> <strong>Created:</strong> {formatDate(purchaseTransaction.created_at)}</div>
          <div><Clock size={14} color="#1890ff" /> <strong>Transaction Date:</strong> {formatDate(purchaseTransaction.transaction_date)}</div>
          {isReturn && purchaseTransaction.return_reason && (
            <div style={{ gridColumn: '1 / -1' }}><RefreshCw size={14} color="#ff4d4f" /> <strong>Return Reason:</strong> {purchaseTransaction.return_reason}</div>
          )}
        </div>

        {isFinanciallyPosted && (
          <div
            style={{
              marginBottom: 20,
              padding: 16,
              border: "1px solid #faad14",
              borderRadius: 8,
              background: "#fffbe6",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <AlertTriangle
                size={22}
                color="#d48806"
              />

              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: 700,
                    marginBottom: 6,
                    color: "#8a5a00",
                  }}
                >
                  Full Posted Purchase Adjustment
                </div>

                <div
                  style={{
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: "#595959",
                  }}
                >
                  The complete form is editable. You may correct the
                  warehouse, supplier, products, quantities, unit costs,
                  discounts, tax, charges, dates, and payment accounts.
                  Saving sends the complete corrected purchase to the
                  backend, which removes the old stock effect, applies the
                  corrected stock effect, and posts balanced accounting
                  adjustment journals without deleting the original audit
                  trail.
                </div>

                {!canUnlockFinancialEditing && (
                  <div
                    style={{
                      marginTop: 10,
                      color: "#cf1322",
                      fontWeight: 600,
                    }}
                  >
                    Your role does not normally include posted-purchase
                    editing. The backend will still enforce its permission
                    rules when you save.
                  </div>
                )}

                <div style={{ marginTop: 14 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 5,
                      fontWeight: 600,
                    }}
                  >
                    Adjustment Reason *
                  </label>

                  <textarea
                    value={adjustmentReason}
                    onChange={(event) =>
                      setAdjustmentReason(
                        event.target.value
                      )
                    }
                    rows={3}
                    placeholder="Explain the original mistake and the correction, for example: Wrong warehouse and quantity selected during purchase entry."
                    style={{
                      width: "100%",
                      padding: 10,
                      borderRadius: 4,
                      border: errors.adjustment_reason
                        ? "1px solid #ff4d4f"
                        : "1px solid #d9d9d9",
                    }}
                  />

                  {errors.adjustment_reason && (
                    <small
                      style={{
                        color: "#ff4d4f",
                      }}
                    >
                      {errors.adjustment_reason}
                    </small>
                  )}

                  {!errors.adjustment_reason && (
                    <small
                      style={{
                        color:
                          adjustmentReason.trim().length >= 10
                            ? "#389e0d"
                            : "#8c8c8c",
                      }}
                    >
                      At least 10 characters are required for the
                      accounting audit trail.
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Warehouse *</label>
              <Select
                value={
                  warehouses
                    .filter(
                      (wh) =>
                        String(wh._id) === String(formData.warehouse_id)
                    )
                    .map((wh) => ({
                      value: wh._id,
                      label: `${wh.warehouse_code} - ${wh.warehouse_name}`,
                    }))[0] || null
                }

                options={warehouses.map((wh) => ({
                  value: wh._id,
                  label: `${wh.warehouse_code} - ${wh.warehouse_name}`,
                }))}

                onChange={(selected) => {
                  const value = selected ? selected.value : "";
                  setFormData(prev => ({ ...prev, warehouse_id: value }));
                  if (value) {
                    fetchProductsForWarehouse(value, products);
                  } else {
                    setFilteredProducts(products);
                    setProductStock({});
                  }
                }}

                placeholder={
                  warehouses.length === 0
                    ? "No warehouses available"
                    : "Search Warehouse *"
                }

                isDisabled={warehouses.length === 0}

                isClearable

                noOptionsMessage={() =>
                  "No warehouse found"
                }

                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }),

                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),

                  menuList: (base) => ({
                    ...base,
                    maxHeight: 250,
                  }),
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Supplier *</label>
              <Select
                value={
                  suppliers
                    .filter(
                      (sup) =>
                        String(sup._id) === String(formData.supplier_id)
                    )
                    .map((sup) => ({
                      value: sup._id,
                      label: `${sup.supplier_code} - ${sup.supplier_name}`,
                    }))[0] || null
                }

                options={suppliers.map((sup) => ({
                  value: sup._id,
                  label: `${sup.supplier_code} - ${sup.supplier_name}`,
                }))}

                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    supplier_id: selected ? selected.value : "",
                  }))
                }

                placeholder={
                  suppliers.length === 0
                    ? "No suppliers available"
                    : "Search Supplier *"
                }

                isDisabled={suppliers.length === 0}

                isClearable

                noOptionsMessage={() =>
                  "No supplier found"
                }

                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }),

                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),

                  menuList: (base) => ({
                    ...base,
                    maxHeight: 250,
                  }),
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Transaction Type *</label>
              <input type="text" value={transactionTypes.find(t => t._id === formData.transaction_type_id)?.transaction_type || purchaseTransaction?.transaction_type_id?.transaction_type || 'Transaction Type'} disabled style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }} />
              <small style={{ color: '#ff4d4f' }}>Transaction type cannot be changed</small>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Invoice No *</label>
              <input type="text" name="invoice_no" value={formData.invoice_no} onChange={handleChange} placeholder="Invoice Number" required style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Transaction Date *</label>
              <input type="date" name="invoice_date" value={formData.invoice_date} onChange={handleChange} required style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Due Date</label>
              <input type="date" name="due_date" value={formData.due_date} onChange={handleChange} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
            </div>
            {isReturn && (
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Return Reason</label>
                <input type="text" name="return_reason" value={formData.return_reason} onChange={handleChange} placeholder="Reason for return" style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
              </div>
            )}
          </div>

          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 15 }}>
            <Package size={18} /> Products
            {formData.warehouse_id && <span style={{ fontSize: 13, color: '#666', fontWeight: 'normal' }}>(Showing stock for selected warehouse)</span>}
          </h3>
          {errors.products && <div style={{ marginBottom: '15px', padding: '10px', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '4px', color: '#ff4d4f', display: 'flex', alignItems: 'center', gap: '8px' }}><AlertCircle size={16} />{errors.products}</div>}

          {productsData.map((product, index) => {
            const currentProduct = products.find(p => String(p._id) === String(product.product_id));

            const selectedValue = currentProduct ? {
              value: currentProduct._id,
              label: `${currentProduct.product_name} (${currentProduct.product_code})` +
                (productStock[currentProduct._id]
                  ? ` | Stock: ${productStock[currentProduct._id].available_stock}`
                  : "") +
                (productStock[currentProduct._id]?.avg_cost !== undefined
                  ? ` | Avg Cost: ${formatCurrency(productStock[currentProduct._id].avg_cost)}`
                  : ""),
            } : null;

            const options = [
              ...(currentProduct && !filteredProducts.some(p => String(p._id) === String(currentProduct._id))
                ? [{
                  value: currentProduct._id,
                  label: `${currentProduct.product_name} (${currentProduct.product_code})` +
                    (productStock[currentProduct._id]
                      ? ` | Stock: ${productStock[currentProduct._id].available_stock}`
                      : "") +
                    (productStock[currentProduct._id]?.avg_cost !== undefined
                      ? ` | Avg Cost: ${formatCurrency(productStock[currentProduct._id].avg_cost)}`
                      : ""),
                }]
                : []),
              ...filteredProducts
                .filter(prod => {
                  if (String(prod._id) === String(product.product_id)) return false;
                  return !productsData.some(
                    (row, rowIndex) => rowIndex !== index && String(row.product_id) === String(prod._id)
                  );
                })
                .map(prod => ({
                  value: prod._id,
                  label: `${prod.product_name} (${prod.product_code})` +
                    (productStock[prod._id]
                      ? ` | Stock: ${productStock[prod._id].available_stock}`
                      : "") +
                    (productStock[prod._id]?.avg_cost !== undefined
                      ? ` | Avg Cost: ${formatCurrency(productStock[prod._id].avg_cost)}`
                      : ""),
                })),
            ];

            return (
              <div key={product._id || `new-${index}`} style={{ marginBottom: '20px', padding: '20px', background: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0', position: 'relative' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '4fr 1fr 1.4fr 1.2fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Product *</label>
                    <Select
                      value={selectedValue}
                      options={options}
                      onChange={(selected) =>
                        handleProductSelect(
                          index,
                          selected ? selected.value : ""
                        )
                      }
                      placeholder={
                        !formData.warehouse_id
                          ? "Select warehouse first"
                          : loadingProducts
                            ? "Loading products..."
                            : filteredProducts.length === 0
                              ? "No products available"
                              : "Search Product..."
                      }
                      isDisabled={
                        !formData.warehouse_id ||
                        loadingProducts
                      }
                      isClearable
                      noOptionsMessage={() =>
                        "No available products"
                      }
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "38px",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menuList: (base) => ({
                          ...base,
                          maxHeight: 250,
                        }),
                      }}
                    />
                    {product.product_id && productStock[product.product_id] && (
                      <div style={{ fontSize: 11, marginTop: 4, color: '#52c41a' }}>
                        <Layers size={12} style={{ display: 'inline', marginRight: 4 }} />
                        Available: {productStock[product.product_id].available_stock} units
                        {!isReturn && productStock[product.product_id].avg_cost > 0 && <> | Avg Cost: {formatCurrency(productStock[product.product_id].avg_cost)}</>}
                      </div>
                    )}
                  </div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Quantity *</label><input
                    type="number"
                    value={product.quantity}
                    onChange={(e) => {
                      handleProductChange(
                        index,
                        "quantity",
                        e.target.value
                      );

                      if (errors.products) {
                        setErrors((previous) => ({
                          ...previous,
                          products: "",
                        }));
                      }
                    }}
                    placeholder="0"
                    required
                    min="0.0001"
                    step="any"
                    disabled={submitting}
                    style={{
                      width: "100%",
                      padding: 8,
                      borderRadius: 4,
                      border: "1px solid #ddd",
                    }}
                  /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Unit Cost *</label><input type="number" value={product.unit_cost} onChange={(e) => handleProductChange(index, 'unit_cost', e.target.value)} placeholder="0.00" required min="0" step="0.01" style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Total</label><input type="text" value={formatCurrency(Number(product.total_cost) || 0)} readOnly style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5', fontWeight: 600, color: isReturn ? '#ff4d4f' : '#1890ff' }} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                  <input type="text" placeholder="Batch Number (optional)" value={product.batch_number} onChange={(e) => handleProductChange(index, 'batch_number', e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
                  <input type="date" placeholder="Expiry Date" value={product.expiry_date} onChange={(e) => handleProductChange(index, 'expiry_date', e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
                </div>
                {index > 0 && (
                  <button type="button" onClick={() => removeProductRow(index)} style={{ position: 'absolute', top: '10px', right: '10px', padding: '4px 8px', background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: '4px', color: '#ff4d4f', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }} title="Remove Product"><Trash2 size={14} /> Remove</button>
                )}
              </div>
            );
          })}

          <div style={{ marginBottom: '30px' }}>
            <button type="button" onClick={addProductRow} style={{ padding: '10px 20px', background: 'transparent', border: '2px dashed #667eea', borderRadius: '6px', color: '#667eea', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600 }} disabled={!formData.warehouse_id}><Plus size={16} /> Add Another Product</button>
            {!formData.warehouse_id && <small style={{ display: 'block', marginTop: '5px', color: '#fa8c16' }}>Please select a warehouse first to add products</small>}
          </div>

          {!isReturn && (
            <>
              <h3 style={{ marginBottom: 15 }}>
                Purchase Discount and Tax
              </h3>

              <div
                style={{
                  padding: 18,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  marginBottom: 20,
                  background: "#fafafa",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 20,
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                      }}
                    >
                      Apply Discount to All Items
                    </label>

                    <select
                      value={purchaseAdjustments.discount_id}
                      onChange={(event) =>
                        setPurchaseAdjustments(
                          (previous) => ({
                            ...previous,
                            discount_id: event.target.value,
                          }),
                        )
                      }
                      style={{
                        width: "100%",
                        padding: 10,
                        borderRadius: 4,
                        border: "1px solid #ddd",
                      }}
                    >
                      <option value="">No Discount</option>

                      {discounts.map((discount) => (
                        <option
                          key={discount._id}
                          value={discount._id}
                        >
                          {discount.name} ({discount.code}) -{" "}
                          {discount.discount_type ===
                            "percentage"
                            ? `${discount.value}%`
                            : formatCurrency(discount.value)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                      }}
                    >
                      Apply Tax to All Items
                    </label>

                    <select
                      value={purchaseAdjustments.tax_id}
                      onChange={(event) =>
                        setPurchaseAdjustments(
                          (previous) => ({
                            ...previous,
                            tax_id: event.target.value,
                          }),
                        )
                      }
                      style={{
                        width: "100%",
                        padding: 10,
                        borderRadius: 4,
                        border: "1px solid #ddd",
                      }}
                    >
                      <option value="">No Tax</option>

                      {taxes.map((tax) => (
                        <option
                          key={tax._id}
                          value={tax._id}
                        >
                          {tax.name} ({tax.code}) -{" "}
                          {tax.tax_type === "percentage"
                            ? `${tax.value}%`
                            : formatCurrency(tax.value)}
                          {tax.price_inclusive
                            ? " · Inclusive"
                            : " · Exclusive"}
                          {tax.recoverable
                            ? " · Recoverable"
                            : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                      }}
                    >
                      Manual Discount Amount
                    </label>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Example: 5.00"
                      value={
                        purchaseAdjustments.manual_discount
                      }
                      onChange={(event) =>
                        setPurchaseAdjustments(
                          (previous) => ({
                            ...previous,
                            manual_discount:
                              event.target.value,
                          }),
                        )
                      }
                      style={{
                        width: "100%",
                        padding: 10,
                        borderRadius: 4,
                        border: "1px solid #ddd",
                      }}
                    />

                    <small style={{ color: "#666" }}>
                      This amount is added to the selected
                      discount and allocated across items.
                    </small>
                  </div>

                  {/* Manual Tax input removed */}
                </div>
              </div>
            </>
          )}

          <h3 style={{ marginBottom: 15 }}>Additional Charges</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              marginBottom: 20,
            }}
          >
            <div>
              <label style={{ display: "block", marginBottom: 5 }}>
                Shipping Cost
              </label>
              <input
                type="number"
                name="shipping_cost"
                value={formData.shipping_cost}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 5 }}>
                Other Charges
              </label>
              <input
                type="number"
                name="other_charges"
                value={formData.other_charges}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              />
            </div>
          </div>

          <h3 style={{ marginBottom: 15 }}>Payments</h3>

          {errors.payments && (
            <div
              style={{
                marginBottom: 15,
                padding: 10,
                background: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: 4,
                color: "#ff4d4f",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <AlertCircle size={16} />
              {errors.payments}
            </div>
          )}

          {loadingPaymentMethods ? (
            <div style={{ padding: 20, textAlign: "center" }}>
              <Loader2 className="animate-spin" size={24} />
              <p>Loading payment accounts...</p>
            </div>
          ) : paymentMethodsData.methods.length === 0 ? (
            <div
              style={{
                padding: 20,
                background: "#fafafa",
                borderRadius: 8,
                marginBottom: 20,
                textAlign: "center",
                border: "1px dashed #ddd",
              }}
            >
              <AlertCircle size={32} color="#fa8c16" />
              <p style={{ marginTop: 10, color: "#666" }}>
                No approved Cash, Bank, Mobile Money, or Other
                posting accounts are configured.
              </p>
            </div>
          ) : (
            <>
              {payments.map((payment, idx) => (
                <div
                  key={idx}
                  style={{
                    marginBottom: 15,
                    padding: 15,
                    background: "#fafafa",
                    borderRadius: 8,
                    border: "1px solid #f0f0f0",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 2fr 1fr auto",
                      gap: 10,
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 12,
                          color: "#666",
                          marginBottom: 4,
                          display: "block",
                        }}
                      >
                        Payment Method
                      </label>
                      <select
                        value={payment.method || ""}
                        onChange={(e) =>
                          handlePaymentChange(
                            idx,
                            "method",
                            e.target.value,
                          )
                        }
                        style={{
                          width: "100%",
                          padding: 8,
                          borderRadius: 4,
                          border: "1px solid #ddd",
                        }}
                      >
                        <option value="">
                          Select Payment Method
                        </option>
                        {paymentMethodsData.methods.map((method) => (
                          <option
                            key={method.key}
                            value={method.key}
                          >
                            {method.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        style={{
                          fontSize: 12,
                          color: "#666",
                          marginBottom: 4,
                          display: "block",
                        }}
                      >
                        Account
                      </label>
                      <select
                        value={payment.account_id || ""}
                        onChange={(e) =>
                          handlePaymentChange(
                            idx,
                            "account_id",
                            e.target.value,
                          )
                        }
                        disabled={payment.method === "credit"}
                        required={
                          payment.method !== "credit" &&
                          Number(payment.amount) > 0
                        }
                        style={{
                          width: "100%",
                          padding: 8,
                          borderRadius: 4,
                          border: "1px solid #ddd",
                          background:
                            payment.method === "credit"
                              ? "#f5f5f5"
                              : "white",
                        }}
                      >
                        <option value="">
                          {payment.method === "credit"
                            ? "Accounts Payable (automatic)"
                            : "Select Account"}
                        </option>
                        {getAccountsForMethod(payment.method).map(
                          (account) => (
                            <option
                              key={account._id}
                              value={account._id}
                            >
                              {account.account_code} -{" "}
                              {account.account_name} (Balance:{" "}
                              {formatCurrency(account.balance || 0)})
                            </option>
                          ),
                        )}
                      </select>
                    </div>

                    <div>
                      <label
                        style={{
                          fontSize: 12,
                          color: "#666",
                          marginBottom: 4,
                          display: "block",
                        }}
                      >
                        Amount
                      </label>
                      <input
                        type="number"
                        placeholder={
                          payment.method === "credit"
                            ? "Remaining balance"
                            : "0.00"
                        }
                        value={payment.amount}
                        onChange={(e) =>
                          handlePaymentChange(
                            idx,
                            "amount",
                            e.target.value,
                          )
                        }
                        disabled={payment.method === "credit"}
                        min="0"
                        step="0.01"
                        style={{
                          width: "100%",
                          padding: 8,
                          borderRadius: 4,
                          border: "1px solid #ddd",
                          background:
                            payment.method === "credit"
                              ? "#f5f5f5"
                              : "white",
                        }}
                      />
                    </div>

                    {payments.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePaymentRow(idx)}
                        style={{
                          marginTop: 20,
                          padding: "8px 16px",
                          background: "#fff1f0",
                          border: "1px solid #ffccc7",
                          borderRadius: 4,
                          color: "#ff4d4f",
                          cursor: "pointer",
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <input
                      type="text"
                      placeholder="Reference / Cheque No (optional)"
                      value={payment.reference || ""}
                      onChange={(e) =>
                        handlePaymentChange(
                          idx,
                          "reference",
                          e.target.value,
                        )
                      }
                      style={{
                        width: "100%",
                        padding: 8,
                        borderRadius: 4,
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                </div>
              ))}

              <div style={{ marginBottom: 30 }}>
                <button
                  type="button"
                  onClick={addPaymentRow}
                  style={{
                    padding: "8px 16px",
                    background: "transparent",
                    border: "2px dashed #52c41a",
                    borderRadius: 6,
                    color: "#52c41a",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 14,
                  }}
                >
                  <Plus size={16} />
                  Add Another Payment Method
                </button>
              </div>
            </>
          )}

          {paymentStatus === "partial" && (
            <div style={{
              marginBottom: 20,
              padding: "12px 16px",
              borderRadius: 4,
              background: "#e6f7ff",
              border: "1px solid #91d5ff",
              color: "#0050b3",
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
            }}>
              <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 14, lineHeight: 1.5 }}>
                Partial payment selected. The remaining supplier balance will be recorded as Accounts Payable.
              </span>
            </div>
          )}

          {paymentStatus === "notpaid" && (
            <div style={{
              marginBottom: 20,
              padding: "12px 16px",
              borderRadius: 4,
              background: "#fffbe6",
              border: "1px solid #ffe58f",
              color: "#d48806",
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
            }}>
              <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 14, lineHeight: 1.5 }}>
                No immediate payment entered. The full purchase amount will be added to Accounts Payable.
              </span>
            </div>
          )}

          <h3 style={{ marginBottom: 15 }}>Totals Summary</h3>
          <div style={{ background: '#fafafa', padding: '20px', borderRadius: '8px', border: '1px solid #f0f0f0', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}><span>Subtotal:</span><strong>{formatCurrency(totals.subTotal)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}><span>Total Discount:</span><strong style={{ color: '#e53935' }}>-{formatCurrency(totals.totalDiscount)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}><span>Tax:</span><strong>+{formatCurrency(totals.totalTax)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}><span>Shipping:</span><strong>+{formatCurrency(totals.shippingCost)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}><span>Other Charges:</span><strong>+{formatCurrency(totals.otherCharges)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px', paddingTop: '15px', borderTop: '2px solid #e0e0e0', fontSize: '18px', fontWeight: 'bold' }}><span>Grand Total:</span><strong style={{ color: isReturn ? '#ff4d4f' : '#1890ff' }}>{formatCurrency(totals.grandTotal)}</strong></div>
            <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px dashed #d9d9d9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}><span>Amount Paid Now:</span><strong style={{ color: '#52c41a' }}>{formatCurrency(totals.totalAmountPaid)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}><span>Supplier Balance (After This Payment):</span><strong style={{ color: totals.netAmount > 0 ? '#ff4d4f' : '#52c41a', fontSize: '16px' }}>{formatCurrency(totals.netAmount)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px', padding: '8px 12px', background: paymentStatusDisplay.bg, border: `1px solid ${paymentStatusDisplay.color}`, borderRadius: '4px', color: paymentStatusDisplay.color, fontWeight: 'bold' }}>Status: {paymentStatusDisplay.text}</div>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Additional notes..." rows="3" style={{ width: '100%', padding: '12px', borderRadius: 4, border: '1px solid #ddd' }} />
          </div>

          {/* ✅ Overpayment / Over-refund warning — works in BOTH Purchase and Return mode */}
          {hasOverpayment && (
            <div
              style={{
                marginBottom: 15,
                padding: '12px 16px',
                background: '#fff1f0',
                border: '1px solid #ffa39e',
                borderLeft: '4px solid #f5222d',
                borderRadius: 4,
                color: '#cf1322',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                <strong style={{ display: 'block', marginBottom: 4 }}>
                  {isReturn
                    ? "Refund amount cannot exceed the return total."
                    : "Payment amount cannot exceed the purchase total."}
                </strong>
                <div style={{ marginBottom: 4 }}>
                  Total: <strong>{formatCurrency(Math.abs(totals.grandTotal))}</strong>
                  {" • "}
                  Entered: <strong>{formatCurrency(totals.totalAmountPaid)}</strong>
                  {" • "}
                  Excess: <strong style={{ color: '#f5222d' }}>{formatCurrency(overpaidAmount)}</strong>
                </div>
                <div>
                  Reduce the {isReturn ? "refund" : "payment"} by{" "}
                  <strong>{formatCurrency(overpaidAmount)}</strong> to continue.
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '30px' }}>
            <button type="button" onClick={handleCancel} disabled={submitting} style={{ padding: '12px 24px', background: 'white', border: '1px solid #d9d9d9', borderRadius: '6px', color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}><X size={16} /> Cancel</button>
            <button type="submit" disabled={
              submitting ||
              hasOverpayment ||
              transactionStatus === "cancelled" ||
              transactionStatus === "returned"
            } title={hasOverpayment ? `${isReturn ? "Refund" : "Payment"} exceeds total by ${formatCurrency(overpaidAmount)}` : ""} style={{ padding: '12px 24px', background: (transactionStatus === 'cancelled' || transactionStatus === 'returned' || hasOverpayment) ? '#d9d9d9' : '#1890ff', border: 'none', borderRadius: '6px', color: 'white', cursor: (transactionStatus === 'cancelled' || transactionStatus === 'returned' || hasOverpayment) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600, opacity: submitting ? 0.7 : 1 }}>
              {submitting ? <><Loader2 className="animate-spin" size={16} /> Updating...</> : <><Save size={16} /> {isFinanciallyPosted
                ? "Save Full Purchase Adjustment"
                : "Update Purchase"}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPurchaseTransaction;